import { OpenAPI } from './client'
import {
  ApiError,
  type AssayEnvelopeArtifactTokenOut,
  type AssayEnvelopeProvenanceRecordOut,
} from './generated'
import type { ArtifactToken, ProvenanceRecord } from './provenance'
import type { WideExtraction } from './extractions'
import type { TreeKind } from '@/types/extractions'

export interface FetchedAssayEnvelopeWide {
  data: WideExtraction | null
  etag: string
  notModified: boolean
}

async function apiError(response: globalThis.Response, url: string): Promise<ApiError> {
  const contentType = response.headers.get('content-type') ?? ''
  const body = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : await response.text().catch(() => response.statusText)
  return new ApiError(
    { method: 'GET', url },
    {
      url: response.url || url,
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      body,
    },
    `${response.status}: ${response.statusText}`,
  )
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw await apiError(response, url)
  return response.json() as Promise<T>
}

export async function fetchAssayEnvelopeWide(
  projectId: number,
  fileId: string,
  options: { etag?: string } = {},
): Promise<FetchedAssayEnvelopeWide> {
  const headers: Record<string, string> = {}
  if (options.etag) headers['If-None-Match'] = options.etag
  const url = `${OpenAPI.BASE}/projects/${projectId}/assay-envelopes/files/${fileId}/assay-wide`
  const response = await fetch(url, { credentials: 'include', headers })
  if (response.status === 304) {
    return {
      data: null,
      etag: response.headers.get('ETag') ?? options.etag ?? '',
      notModified: true,
    }
  }
  if (!response.ok) {
    throw await apiError(response, url)
  }
  return {
    data: (await response.json()) as WideExtraction,
    etag: response.headers.get('ETag') ?? '',
    notModified: false,
  }
}

export async function fetchAssayEnvelopeProvenance(
  projectId: number,
  fileId: string,
  page: number,
): Promise<ProvenanceRecord[]> {
  return getJson<AssayEnvelopeProvenanceRecordOut[]>(
    `${OpenAPI.BASE}/projects/${projectId}/assay-envelopes/files/${fileId}/provenance/${page}`,
  )
}

export async function fetchAssayEnvelopeArtifacts(
  projectId: number,
  fileId: string,
  page: number,
): Promise<ArtifactToken[]> {
  const artifacts = await getJson<AssayEnvelopeArtifactTokenOut[]>(
    `${OpenAPI.BASE}/projects/${projectId}/assay-envelopes/files/${fileId}/artifacts/${page}`,
  )
  return artifacts.map((artifact) => {
    if (artifact.bbox.length !== 4 || artifact.bbox.some((value) => typeof value !== 'number')) {
      throw new Error('The extraction artifact contains an invalid bounding box.')
    }
    return {
      id: artifact.id,
      text: artifact.text,
      bbox: artifact.bbox as [number, number, number, number],
      ...(artifact.type === null || artifact.type === undefined ? {} : { type: artifact.type }),
      ...(artifact.page === null || artifact.page === undefined ? {} : { page: artifact.page }),
    }
  })
}

export function envelopeSourcePdfUrl(
  projectId: number,
  fileId: string,
  treeKind: TreeKind,
): string {
  return `${OpenAPI.BASE}/projects/${projectId}/assay-envelopes/files/${fileId}/source-pdf?tree_kind=${treeKind}`
}
